import {
  onSnapshot,
  type DocumentData,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';

/**
 * Listen to a query whose current answer the SERVER already rendered into the
 * page, and hand over to the live listener without a stale copy in between.
 *
 * WHY. The product page and the storefront are painted from the server's own
 * read of these documents (src/lib/product-page-seed.ts and
 * src/lib/storefront-seed.ts), and the listener takes over in place. Firestore's
 * persistent cache (src/firebase/client.ts) answers a listener from this
 * browser's IndexedDB before the server has said anything, and that copy can be
 * days old: whatever this visitor last saw. Taking it would swap the server's
 * fresh paint for a stale one and then flip back once the server answered,
 * which is the flash the server render exists to remove.
 *
 * So snapshots served from the cache are skipped until the server has answered
 * once, and every snapshot after that is passed through. `includeMetadataChanges`
 * is what makes that first server answer arrive at all when it matches the
 * cached copy exactly: without it a sync that changes no document raises no
 * event, and the listener would never report.
 *
 * Offline, or with Firestore unreachable, the server never answers, so the page
 * keeps the server's copy rather than falling back to the cache, or to "not
 * found" for a document this browser never cached.
 *
 * For an unseeded query use onSnapshot itself: there the cache's answer is the
 * fastest thing available and is exactly what should be shown.
 */
export function onSnapshotFromServer<T = DocumentData>(
  q: Query<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  let heardFromServer = false;
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      if (!heardFromServer) {
        if (snapshot.metadata.fromCache) return;
        heardFromServer = true;
      }
      onNext(snapshot);
    },
    onError,
  );
}
